/**
 * UC1 "Why these areas" (NOTES #729): how the priority areas were arrived at, in plain
 * language and without picking a landscape — the rule in one sentence, then every High
 * Priority and Moderate watershed with the constraints that put it there, then the Low
 * count. Built from the watershed rows' own `flags`.
 */
import { useTranslation } from 'react-i18next';

import type { IntelUnitRow } from '../../lib/api';
import { UC1_DIMENSIONS } from './uc1Constants';

function flaggedNames(row: IntelUnitRow, t: (k: string) => string): string {
  return UC1_DIMENSIONS.filter((dim) => row.flags?.[dim] === true)
    .map((dim) => t(`intel.uc1.dimension.${dim}`))
    .join(', ');
}

export function Why({ rows }: { rows: IntelUnitRow[] }) {
  const { t } = useTranslation();
  const byCat = (cat: string) =>
    rows.filter((r) => r.category === cat).sort((a, b) => a.name.localeCompare(b.name));
  const high = byCat('high');
  const moderate = byCat('moderate');
  const low = byCat('low');
  return (
    <div id="intelWhy" className="intel-why result-box">
      <h5>{t('intel.uc1.whyTitle')}</h5>
      <div className="result-pad">
        <p>{t('intel.uc1.why.rule')}</p>
        {[['high', high], ['moderate', moderate]].map(([cat, list]) => (
          <div key={cat as string} data-testid={`uc1-why-${cat}`}>
            <b>{t(`intel.uc1.why.${cat}`, { n: (list as IntelUnitRow[]).length })}</b>
            {(list as IntelUnitRow[]).length ? (
              <ul className="uc1-why-list">
                {(list as IntelUnitRow[]).map((row) => (
                  <li key={row.unit_id}>
                    {t('intel.uc1.why.line', { name: row.name, constraints: flaggedNames(row, t) })}
                  </li>
                ))}
              </ul>
            ) : (
              <p className="sub">{t('intel.uc1.why.none')}</p>
            )}
          </div>
        ))}
        <p data-testid="uc1-why-low">{t('intel.uc1.why.low', { n: low.length })}</p>
        <p className="sub">{t('intel.uc1.why.trace')}</p>
      </div>
    </div>
  );
}

export default Why;
